import { Rule } from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useNotification } from '../../../hooks/NotificationContext';
import useDebounce from '../../../hooks/useDebounce';
import { isExecutiveManager } from '../../../lib/userUtils';
import { createQueryString } from '../../../lib/utils';
import { FilterAltOutlined as FilterAltOutlinedIcon } from '@mui/icons-material';
import {
  buildTestCycleFilterConfig,
  TEST_CYCLE_FILTER_DEFAULTS,
} from '../../../features/testcycles/Testcyclefilterconfig';
import { ActionButton, AppliedFilterChips, FilterModal } from '../../../ui/data/filters';
import { useFilterState } from '../../../hooks/useFilterState';
import useExecutionScope from '../../../hooks/useExecutionScope';
import TestCycleTableActions from '../../../features/testcycles/TestCycleTableActions';
import {
  useJenkinsJobs,
  useProjectUsers,
  useTestCycleConfig,
  useTestCycles,
} from '../../../query/apiQueries';
import TestCycleTable from '../../../ui/data/table/TableComponent';
import SearchBar from '../../../ui/primitives/SearchBar';
import CustomIconButton from '../../elements/CustomIconButton';
import { tableHeaderTestCycles } from '../test-case/testCaseTableHead';
import AddTestCycleModal from './AddTestCycleModal';
import MarkTestCycleComplete from './MarkTestCycleComplete';
import { formatData } from './static-data';
import ExecutionScopeForm from '../../elements/common/execution/ExecutionScopeForm';
import { createFilterPreset, deleteFilterPreset, getFilterPresets } from '../../../modules/datafilters';


const DEFAULT_COLUMNS = [
  'key',
  'name',
  'type',
  'status',
  'totalAssociatedTestCases',
  'totalIterations',
  'progress',
  'lastUpdated',
];

const TestCyclesTabContentComponent = () => {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [showModal, setShowModal] = useState(false);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [searchValue, setSearchValue] = useState(location.state?.searchValue || '');
  const [page, setPage] = useState(location.state?.page || 1);
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const { isScopeDefined } = useExecutionScope(projectId);
  const filterConfig = useMemo(() => buildTestCycleFilterConfig(), []);
  const [savedFilters, setSavedFilters] = useState([]);
  const [savingFilter, setSavingFilter] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('user'));

  const {
    filters,
    setFilters,
    removeFilter,
    clearAllFilters,
    activeCount,
  } = useFilterState(filterConfig, location.state?.filters || TEST_CYCLE_FILTER_DEFAULTS);

  const { data: config = [], isLoading: loadingConfig } = useTestCycleConfig(projectId);
  const { data: projectUsers = [], isLoading: loadingUsers } = useProjectUsers(projectId);
  const { data: jobs = [], isLoading: loadingJobs, error: jobError } = useJenkinsJobs(projectId);

  const optionsMap = useMemo(() => ({
    status: config?.status || [],
    owner: projectUsers.map(u => ({ id: u.id, name: u.full_name })),
    purpose: config?.purpose || [],
    type: config?.type || [],
  }), [config, projectUsers]);

  const [perPage, setPerPage] = useState(10);
  const queryString = createQueryString(filters);

  const {
    data: testCycleResponse,
    isLoading: loadingCycles,
    isFetching: isFetchingCycles,
    refetch,
    error: testCycleError,
  } = useTestCycles(
    {
      page,
      perPage,
      search: debouncedSearchQuery,
      queryString,
      projectId,
    }
  );

  const testcycles = testCycleResponse?.testcycles || [];
  const totalCount = testCycleResponse?.total || 0;
  const isInitialLoading = loadingCycles || loadingUsers || loadingJobs || loadingConfig;

  useEffect(() => {
    if (testCycleError) {
      addNotification('danger', testCycleError.message || 'Failed to fetch test cycles. Please try again later.');
    }
  }, [testCycleError]);

  const handleNavigationStateUpdate = useCallback(() => {
    navigate('.', { replace: true, state: { filters, searchValue, page } });
  }, [filters, searchValue, page, navigate]);

  useEffect(() => {
    handleNavigationStateUpdate();
  }, [debouncedSearchQuery, filters, page, handleNavigationStateUpdate, perPage]);

  const STORAGE_KEY = `table-columns-testcycles-${projectId}`;
  const [selectedColumns, setSelectedColumns] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      const parsed = saved ? JSON.parse(saved) : null;
      return Array.isArray(parsed) && parsed.length > 0 ? parsed : DEFAULT_COLUMNS;
    } catch {
      return DEFAULT_COLUMNS;
    }
  });
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedColumns));
  }, [selectedColumns]);

  const formattedData = useMemo(() => formatData(testcycles), [testcycles]);

  const renderRowActions = useCallback(
    (row) => (
      <>
        {!isExecutiveManager() &&
          <MarkTestCycleComplete
            row={row}
            addNotification={addNotification}
            fetchTestCycles={refetch}
          />
        }
        <CustomIconButton
          title='Go to Test Executions'
          onClick={() => navigate(`/project/${projectId}/testcycles/${row.key}`)}>
          <Rule sx={{ color: '#1b3f87' }} />
        </CustomIconButton>
      </>
    ),
    [refetch, projectId]
  );
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (value) => {
    setSearchValue(value);
    setPage(1);
  };

  const handleRefresh = () => {
    refetch();
    setSelected([]);
    setSelectedRows([]);
  }

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
    navigate('.', {
      replace: true,
      state: { filters, searchValue, page: 1 },
    });
  };

  const handleClearAllFilters = () => {
    setSearchValue('');
    setPage(1);
    clearAllFilters();

    navigate('.', {
      replace: true,
      state: {
        filters: TEST_CYCLE_FILTER_DEFAULTS,
        searchValue: '',
        page: 1,
        showArchived: false,
      },
    });
  };

  const fetchFilterPresets = async () => {
    if (!projectId) return;
    try {
      const response = await getFilterPresets(projectId, 'testcycle');
      setSavedFilters(response?.data || []);
    } catch (error) {
      console.error('Failed to fetch filter presets:', error);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchFilterPresets();
    }
  }, [projectId]);

  const handleSaveFilter = async (name, filterValues, shareLevel = 'private') => {
    setSavingFilter(true);
    try {
      await createFilterPreset(projectId, {
        name,
        filters: filterValues,
        shareLevel,
        filterType: 'testcycle',
      });
      addNotification('success', 'Filter preset saved successfully!');
      fetchFilterPresets();
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to save filter preset.');
    } finally {
      setSavingFilter(false);
    }
  };

  const handleDeleteFilter = async (presetId) => {
    try {
      await deleteFilterPreset(projectId, presetId);
      addNotification('success', 'Filter preset deleted successfully!');
      fetchFilterPresets();
    } catch (error) {
      addNotification('danger', error?.message || 'Failed to delete filter preset.');
    }
  };

  return (
    <Container fluid className="mt-2">
      <Row>
        <Col>
          <Typography variant="h4" className="mb-3">Test Cycles</Typography>
        </Col>
      </Row>
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        wrap="wrap"
        gap={1}
      >
        <Grid item sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search Test Cycles"
          />
          <ActionButton
            onClick={() => setShowFilterModal(true)}
            label="Filters"
            icon={<FilterAltOutlinedIcon />}
            badgeContent={activeCount}
          />
        </Grid>
        {!isExecutiveManager() && (
          <TestCycleTableActions
            handleOpenTestCycleModal={() => setShowModal(true)}
            isSelectedItems={selectedRows?.length}
            selectedItems={selected}
            refreshData={handleRefresh}
            selectedRows={selectedRows}
            addNotification={addNotification}
          />
        )}
      </Grid>
      {(activeCount > 0 || searchValue) && (
        <Box sx={{ mt: 2 }}>
          <AppliedFilterChips
            filters={filters}
            filterConfig={filterConfig}
            optionsMap={optionsMap}
            onRemove={removeFilter}
            onClearAll={handleClearAllFilters}
          />
        </Box>
      )}
      <TestCycleTable
        data={formattedData || []}
        headerTabs={tableHeaderTestCycles}
        showCheckbox={isExecutiveManager() ? false : true}
        page={page}
        setPerPage={handlePageSizeChange}
        perPage={perPage}
        order={order}
        setOrder={setOrder}
        isLoading={isInitialLoading || isFetchingCycles}
        defaultSort="created_at"
        selected={selected}
        setSelected={setSelected}
        setSelectedRows={setSelectedRows}
        totalCount={totalCount}
        showPages
        striped
        handlePageChange={handlePageChange}
        action={renderRowActions}
        tablekey="test cycles"
        showCount
        showRowCount={true}
        showColumnManager
        selectedColumns={selectedColumns}
        setSelectedColumns={setSelectedColumns}
        mandatoryKeys={['key', 'name']}
        defaultColumns={DEFAULT_COLUMNS}
        onRefresh={handleRefresh}
      />
      <AddTestCycleModal
        showModal={showModal}
        handleClose={() => setShowModal(false)}
        fetchTestCycles={handleRefresh}
        addNotification={addNotification}
        statuses={config?.status}
        purposeOptions={config?.purpose}
        typeOptions={config?.type}
        jobs={jobs}
        jobError={jobError}
        projectUsers={projectUsers}
      />
      <FilterModal
        open={showFilterModal}
        onClose={() => setShowFilterModal(false)}
        onApply={(newFilters) => { setFilters(newFilters); setPage(1); }}
        filterConfig={filterConfig}
        currentFilters={filters}
        optionsMap={optionsMap}
        title="Filter Test Cycles"
        showExecutionScope={isScopeDefined}
        ExecutionScopeComponent={isScopeDefined ? <ExecutionScopeForm isPlatformRequired={false} /> : null}
        savedFilters={savedFilters}
        onSaveFilter={handleSaveFilter}
        onDeleteFilter={handleDeleteFilter}
        savingFilter={savingFilter}
        userRole={currentUser?.role}
      />
    </Container>
  );
};

export default TestCyclesTabContentComponent;
